/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import React from 'react';
import {Snackbar} from "./snackbar";
import {CustomAppEvent, DialogConfig, Snack} from "../util/interfaces";
import WithEvents from '../events/withEvents';
import {delay} from 'lodash';

interface Props {
    containerClass: string;
    textClass: string;
    activeTime: number;
    backgroundColor?: string;
    textColor?: string;
    maxSnacks?: number;
    noDuplicates?: boolean;
}

interface State {
    active: boolean;
    snack: Snack | null;
    config: DialogConfig;
}

export default class SnackbarController extends React.Component<Props, State> {
    static defaultProps = {
        activeTime: 3000,
        containerClass: '',
        textClass: '',
    };

    state: State = {
        active: false,
        snack: null,
        config: {}
    };

    snackQueue: Snack[] = [];
    timeout: number | null = null;

    onAddSnack = (snack: Snack) => {
        const {maxSnacks, noDuplicates} = this.props;
        if (noDuplicates && this.isDuplicate(snack)) return;
        this.snackQueue.push(snack);
        if (maxSnacks !== undefined)
            while (this.snackQueue.length > maxSnacks - 1)
                this.snackQueue.shift();
        if (!this.state.active)
            this.activate();
    };

    onClick = () => {
        if (this.timeout !== null)
            window.clearTimeout(this.timeout);
        this.deactivate();
    };

    isDuplicate = (snack: Snack): boolean => {
        return this.state.snack?.text === snack.text || this.snackQueue.pop()?.text === snack.text;
    }

    deactivate = () => {
        this.setState({active: false}, () => {
            delay(() => {
                // Clear snack after 200ms (css transition)
                this.setState({snack: null}, () => {
                    if (this.snackQueue.length > 0)
                        this.timeout = window.setTimeout(this.activate, 500);
                })
            }, 200);
        })
    };

    activate = () => {
        const snack: Snack | undefined = this.snackQueue.pop();
        if (snack) {
            this.setState({snack}, () => {
                this.setState({active: true});
                this.timeout = window.setTimeout(this.deactivate, this.props.activeTime);
            })
        }
    };

    componentWillUnmount = () => {
        if (this.timeout !== null)
            window.clearTimeout(this.timeout);
    };

    getEvents = (): CustomAppEvent[] => [
        {eventName: 'onShowSnackbar', handler: this.onAddSnack}
    ];

    render = () => {
        const {snack, active} = this.state, {containerClass, textClass, textColor, backgroundColor} = this.props;
        return (
            <WithEvents events={this.getEvents()}>
                <Snackbar snack={snack} containerClass={containerClass} textClass={textClass} active={active}
                          onClick={this.onClick} textColor={textColor} backgroundColor={backgroundColor}/>
            </WithEvents>
        )
    };
}